import React, { useState, useEffect } from 'react';
import transactionService from '../services/transactionService';
import accountService from '../services/accountService';
import categoryService from '../services/categoryService';
import virtualTransactionService from '../services/virtualTransactionService';
import { formatError } from '../utils/errorHelper';
import { generateUUID } from '../utils/uuidHelper';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getIconComponent } from '../utils/iconHelper';
import { 
  MdEdit, 
  MdDelete, 
  MdCheckCircle, 
  MdRadioButtonUnchecked,
  MdDescription,
  MdNotes,
  MdArrowUpward,
  MdArrowDownward,
  MdFilterList,
  MdFilterListOff,
  MdToggleOff,
  MdViewList,
  MdCalendarViewMonth
} from 'react-icons/md';
import CategorySelect from '../components/CategorySelect';
import AccountSelect from '../components/AccountSelect';
import DatePicker from '../components/DatePicker';
import CurrencyInput from '../components/CurrencyInput';
import ConfirmDialog from '../components/ConfirmDialog';
import EffectuateTransactionModal from '../components/EffectuateTransactionModal';
import MonthlyTransactionCalendar from '../components/MonthlyTransactionCalendar';
import './Transactions.css';

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'calendar'
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    account_id: '',
    category_id: '',
    type: 'expense',
    is_paid: true,
    notes: '',
    nature: 'individual',
    virtual_transaction_id: null,
    is_recurring: false,
    frequency: 'monthly',
    occurrences: 3,
  });
  const [errors, setErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });
  const [effectuateModal, setEffectuateModal] = useState({
    isOpen: false,
    transaction: null
  });

  // Função para adicionar notificação
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remover após duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  // Função para remover notificação
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    loadData();
  }, [currentDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentDate), 'yyyy-MM-dd');

      // Usar Promise.allSettled para não interromper se uma requisição falhar
      const [monthlyDashboardResult, accountsResult, categoriesResult, summaryResult] =
        await Promise.allSettled([
          transactionService.getMonthlyDashboard(year, month).catch(err => ({ data: [], error: err })),
          accountService.getAccounts().catch(err => ({ data: [], error: err })),
          categoryService.getCategories().catch(err => ({ data: [], error: err })),
          transactionService.getSummary({ start_date: start, end_date: end }).catch(err => ({ data: {}, error: err })),
        ]);

      // Extrair dados de cada resultado, com fallback para valores vazios
      const monthlyDashboardData = monthlyDashboardResult.status === 'fulfilled' 
        ? monthlyDashboardResult.value 
        : { data: [] };
      
      const accountsData = accountsResult.status === 'fulfilled' 
        ? accountsResult.value 
        : { data: [] };
      
      const categoriesData = categoriesResult.status === 'fulfilled' 
        ? categoriesResult.value 
        : { data: [] };
      
      const summaryData = summaryResult.status === 'fulfilled' 
        ? summaryResult.value 
        : { data: {} };

      // Carregar saldo de cada conta
      const accountsArray = Array.isArray(accountsData?.data) 
        ? accountsData.data 
        : Array.isArray(accountsData) 
          ? accountsData 
          : [];
      
      const accountsWithBalance = accountsArray.length > 0
        ? await Promise.all(
            accountsArray.map(async (account) => {
              try {
                const balanceData = await accountService.getAccountBalance(account.id);
                return {
                  ...account,
                  current_balance: balanceData.data?.balance || balanceData.balance || 0,
                };
              } catch (error) {
                console.error(`Erro ao carregar saldo da conta ${account.id}:`, error);
                return {
                  ...account,
                  current_balance: 0,
                };
              }
            })
          )
        : [];

      setTransactions(monthlyDashboardData?.data || []);
      setAccounts(accountsWithBalance);
      setCategories(Array.isArray(categoriesData?.data) ? categoriesData.data : (Array.isArray(categoriesData) ? categoriesData : []));
      
      // Garantir que summary sempre tenha a estrutura esperada
      const summaryResponse = summaryData?.data || summaryData || {};
      setSummary({
        income: summaryResponse.income || summaryResponse.paid_income || 0,
        expense: summaryResponse.expense || summaryResponse.paid_expenses || 0,
        balance: summaryResponse.balance || (summaryResponse.paid_income || 0) - (summaryResponse.paid_expenses || 0),
        pending_income: summaryResponse.pending_income || 0,
        pending_expenses: summaryResponse.pending_expenses || 0,
      });
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      addNotification('error', 'Erro ao Carregar', 'Erro ao carregar dados. Verifique sua conexão.', 6000);
    } finally {
      setLoading(false);
    }
  };

  const previousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Validação básica no frontend
    const validationErrors = {};
    if (!formData.description || formData.description.trim() === '') {
      validationErrors.description = 'A descrição é obrigatória';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      validationErrors.amount = 'O valor deve ser maior que zero';
    }
    if (!formData.account_id) {
      validationErrors.account_id = 'A conta é obrigatória';
    }
    if (!formData.category_id) {
      validationErrors.category_id = 'A categoria é obrigatória';
    }
    if (!formData.date) {
      validationErrors.date = 'A data é obrigatória';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      addNotification('error', 'Campos Obrigatórios', 'Por favor, preencha todos os campos obrigatórios antes de continuar.');
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        amount: parseFloat(formData.amount),
        nature: formData.is_recurring ? 'recurring' : 'individual',
      };

      // Se for recorrente, adicionar os campos de recorrência
      if (formData.is_recurring) {
        dataToSend.frequency = formData.frequency;
        dataToSend.occurrences = parseInt(formData.occurrences);
        
        // Gerar UUID para recurrence_id
        dataToSend.recurrence_id = generateUUID();
      }

      if (editingTransaction) {
        await transactionService.updateTransaction(editingTransaction.id, dataToSend);
      } else {
        // Se for recorrente, criar múltiplas transações
        if (formData.is_recurring) {
          const transactions = generateRecurringTransactions(dataToSend);
          const result = await transactionService.batchCreateTransactions({ transactions });
          
          // Verificar se houve erros no batch create
          if (result?.total_errors > 0) {
            // Extrair apenas a primeira linha do erro (remover detalhes técnicos)
            const errorMessages = result?.errors?.map((err) => {
              const message = err.message || 'Erro desconhecido';
              // Pegar apenas a primeira linha do erro
              const firstLine = message.split('\n')[0].split('ERROR:')[0].trim();
              return `• Transação ${err.index + 1}: ${firstLine}`;
            }).join('\n') || 'Erro ao criar transações recorrentes';
            
            if (result?.total_created === 0) {
              // Nenhuma transação foi criada - erro total
              const errorDetail = result?.errors?.[0]?.message?.split('ERROR:')?.[1]?.split('DETAIL:')?.[0]?.trim() || 'Erro no banco de dados';
              addNotification(
                'error',
                'Falha ao Criar Transações',
                `Nenhuma transação foi criada.\n\nMotivo: ${errorDetail}`,
                8000
              );
              return; // Não fechar o formulário se nada foi criado
            } else {
              // Sucesso parcial
              addNotification(
                'warning',
                'Transações Criadas Parcialmente',
                `✓ Criadas: ${result?.total_created}/${transactions.length}\n✗ Com erro: ${result?.total_errors}\n\n${errorMessages}`,
                8000
              );
              // Recarregar para mostrar as que foram criadas
              loadData();
              // Fechar o formulário e não mostrar notificação de sucesso adicional
              setShowForm(false);
              setEditingTransaction(null);
              resetForm();
              return;
            }
          }
        } else {
          await transactionService.createTransaction(dataToSend);
        }
      }

      // Notificação de sucesso
      if (editingTransaction) {
        addNotification('success', 'Sucesso', 'Transação atualizada com sucesso!', 3000);
      } else if (formData.is_recurring) {
        addNotification('success', 'Sucesso', `${dataToSend.occurrences} transações recorrentes criadas com sucesso!`, 4000);
      } else {
        addNotification('success', 'Sucesso', 'Transação criada com sucesso!', 3000);
      }

      setShowForm(false);
      setEditingTransaction(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      
      // Extrai erros de validação do backend
      if (error?.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        
        // Verificar se são erros de transações em lote (transactions.0.field, transactions.1.field, etc)
        const isBatchError = Object.keys(backendErrors).some(key => key.startsWith('transactions.'));
        
        if (isBatchError) {
          // Agrupar erros por campo
          const errorsByField = {};
          Object.keys(backendErrors).forEach((key) => {
            // Extrair o nome do campo (ex: transactions.0.category_id -> category_id)
            const fieldMatch = key.match(/transactions\.\d+\.(.+)/);
            if (fieldMatch) {
              const fieldName = fieldMatch[1];
              if (!errorsByField[fieldName]) {
                errorsByField[fieldName] = [];
              }
              const errorMessage = Array.isArray(backendErrors[key]) ? backendErrors[key][0] : backendErrors[key];
              errorsByField[fieldName].push(errorMessage);
            }
          });
          
          // Criar mensagem de erro clara
          const fieldNames = {
            'category_id': 'Categoria',
            'account_id': 'Conta',
            'amount': 'Valor',
            'description': 'Descrição',
            'date': 'Data',
            'type': 'Tipo'
          };
          
          const errorMessages = Object.keys(errorsByField).map(field => {
            const friendlyName = fieldNames[field] || field;
            return `• ${friendlyName}: ${errorsByField[field][0].replace(`The transactions.0.${field} field`, 'O campo')}`;
          }).join('\n');
          
          addNotification(
            'error',
            'Erro de Validação',
            `Foram encontrados erros de validação:\n\n${errorMessages}\n\nPor favor, corrija os campos e tente novamente.`,
            8000
          );
          
          // Mapear erros para os campos do formulário
          const formErrors = {};
          Object.keys(errorsByField).forEach(field => {
            formErrors[field] = errorsByField[field][0].replace(/^The transactions\.\d+\.[^ ]+ field/, 'Este campo');
          });
          setErrors(formErrors);
        } else {
          // Erros de validação de transação única
          const formErrors = {};
          Object.keys(backendErrors).forEach((field) => {
            const fieldErrors = backendErrors[field];
            formErrors[field] = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
          });
          setErrors(formErrors);
        }
      } else if (error.message && error.message.includes('Falha ao criar transações recorrentes')) {
        // Erro específico de transações recorrentes
        addNotification('error', 'Erro ao Salvar', error.message, 8000);
      } else {
        // Se não houver erros específicos, mostra uma notificação geral
        addNotification('error', 'Erro ao Salvar', formatError(error, 'Erro ao salvar transação. Verifique os dados.'), 6000);
      }
    }
  };

  const handleEdit = (transaction) => {
    // Não permitir editar transações virtuais
    if (transaction.is_virtual) {
      addNotification('info', 'Transação Virtual', 'Transações virtuais devem ser editadas na tela de Transações Virtuais', 5000);
      return;
    }
    
    setEditingTransaction(transaction);
    setFormData({
      description: transaction.description,
      amount: transaction.amount.toString(),
      date: transaction.date,
      account_id: transaction.account_id,
      category_id: transaction.category_id,
      type: transaction.type,
      is_paid: transaction.is_paid,
      notes: transaction.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id, isVirtual) => {
    if (isVirtual) {
      addNotification('info', 'Transação Virtual', 'Transações virtuais devem ser gerenciadas na tela de Transações Virtuais', 5000);
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Transação',
      message: 'Deseja realmente excluir esta transação?\n\nEsta ação não pode ser desfeita.',
      type: 'error',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await transactionService.deleteTransaction(id);
          addNotification('success', 'Sucesso', 'Transação excluída com sucesso!', 3000);
          loadData();
        } catch (error) {
          console.error('Erro ao excluir transação:', error);
          addNotification('error', 'Erro ao Excluir', 'Erro ao excluir transação.', 5000);
        }
      }
    });
  };

  const handleEffectuateVirtual = (transaction) => {
    // Preencher o formulário com os dados da transação virtual
    setFormData({
      description: transaction.description,
      amount: transaction.amount.toString(),
      date: transaction.date,
      account_id: transaction.account_id,
      category_id: transaction.category_id,
      type: transaction.type,
      is_paid: true,
      notes: '',
      nature: 'fixed',
      virtual_transaction_id: transaction.virtual_transaction_id,
    });
    setShowForm(true);
  };

  const handleInactivateVirtual = async (transaction) => {
    setConfirmDialog({
      isOpen: true,
      title: '⚠️ Inativar Transação Fixa',
      message: 
        `Ao inativar esta transação fixa:\n\n` +
        `• Todos os meses pendentes de efetivação desta transação irão DESAPARECER da listagem\n` +
        `• A transação fixa "${transaction.description}" será marcada como INATIVA\n` +
        `• Você pode reativá-la posteriormente na tela de Transações Fixas\n\n` +
        `Deseja realmente inativar esta transação fixa?`,
      type: 'warning',
      confirmText: 'Sim, Inativar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          // Inativar a transação virtual através da API
          await virtualTransactionService.toggleActive(transaction.virtual_transaction_id);
          
          addNotification(
            'success', 
            'Transação Fixa Inativada', 
            `A transação fixa "${transaction.description}" foi inativada com sucesso. Os meses pendentes não aparecerão mais na listagem.`,
            4000
          );
          
          // Recarregar dados para remover as transações virtuais inativas
          loadData();
        } catch (error) {
          console.error('Erro ao inativar transação fixa:', error);
          addNotification(
            'error',
            'Erro ao Inativar',
            'Erro ao inativar transação fixa. Tente novamente.',
            5000
          );
        }
      }
    });
  };

  const togglePaid = async (transaction) => {
    // Se a transação está pendente (não paga), abre a modal de liquidação
    if (!transaction.is_paid) {
      setEffectuateModal({
        isOpen: true,
        transaction: transaction
      });
    } else {
      // Se já está paga, apenas faz o toggle para marcar como pendente
      try {
        await transactionService.togglePaid(transaction.id);
        addNotification('success', 'Status Atualizado', 'Transação marcada como pendente', 3000);
        loadData();
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        addNotification('error', 'Erro ao Atualizar', 'Erro ao atualizar status da transação', 5000);
      }
    }
  };

  const handleEffectuateTransaction = async (formData) => {
    const transaction = effectuateModal.transaction;
    
    try {
      // Atualizar a transação com os novos dados e marcar como paga
      await transactionService.updateTransaction(transaction.id, {
        ...formData,
        is_paid: true,
        type: transaction.type,
        category_id: transaction.category_id,
        nature: transaction.nature,
        virtual_transaction_id: transaction.virtual_transaction_id,
        recurrence_id: transaction.recurrence_id,
        current_occurrence: transaction.current_occurrence,
        occurrences: transaction.occurrences,
        frequency: transaction.frequency
      });
      
      addNotification('success', 'Transação Liquidada', 'Transação marcada como paga com sucesso!', 3000);
      loadData();
    } catch (error) {
      console.error('Erro ao liquidar transação:', error);
      addNotification('error', 'Erro ao Liquidar', formatError(error, 'Erro ao liquidar transação'), 5000);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      account_id: '',
      category_id: '',
      type: 'expense',
      is_paid: true,
      notes: '',
      nature: 'individual',
      virtual_transaction_id: null,
      is_recurring: false,
      frequency: 'monthly',
      occurrences: 3,
    });
    setErrors({});
  };

  // Função para calcular a próxima data baseada na frequência
  const getNextDate = (currentDate, frequency) => {
    // Garantir que a data seja tratada como data local, não UTC
    // Se não tiver 'T', adicionar para evitar problemas de timezone
    const dateString = currentDate.includes('T') ? currentDate : currentDate + 'T00:00:00';
    const date = new Date(dateString);
    
    switch (frequency) {
      case 'daily':
        date.setDate(date.getDate() + 1);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'biweekly':
        // Adicionar exatamente 15 dias à data anterior
        date.setDate(date.getDate() + 15);
        break;
      case 'monthly':
        // Manter o mesmo dia do mês (ou último dia se não existir)
        date.setMonth(date.getMonth() + 1);
        break;
      case 'bimonthly':
        date.setMonth(date.getMonth() + 2);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'semiannual':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        date.setMonth(date.getMonth() + 1);
    }
    
    return format(date, 'yyyy-MM-dd');
  };

  // Função para gerar múltiplas transações recorrentes
  const generateRecurringTransactions = (baseTransaction) => {
    const transactions = [];
    let currentDate = baseTransaction.date;
    
    for (let i = 0; i < baseTransaction.occurrences; i++) {
      transactions.push({
        ...baseTransaction,
        date: currentDate,
        current_occurrence: i + 1,
        // Apenas a primeira transação herda o is_paid, as demais são sempre pendentes
        is_paid: i === 0 ? baseTransaction.is_paid : false,
      });
      
      if (i < baseTransaction.occurrences - 1) {
        currentDate = getNextDate(currentDate, baseTransaction.frequency);
      }
    }
    
    return transactions;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      // Tenta criar a data de diferentes formas
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T00:00:00');
      }
      
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return dateString || '-';
    }
  };

  const formatDateHeader = (dateString) => {
    try {
      if (!dateString) return '';
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T00:00:00');
      }
      
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Formatar como "Segunda-feira, 13 de Janeiro de 2026"
      return format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })
        .replace(/^\w/, (c) => c.toUpperCase());
    } catch (error) {
      console.error('Erro ao formatar data do cabeçalho:', dateString, error);
      return dateString || '';
    }
  };

  const groupTransactionsByDate = (transactions) => {
    const groups = {};
    
    transactions.forEach(transaction => {
      // Normalizar a data para formato YYYY-MM-DD (remove timestamp se houver)
      let dateKey = transaction.date;
      if (dateKey.includes('T')) {
        dateKey = dateKey.split('T')[0];
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          transactions: [],
          totalPaid: 0,
          totalPending: 0
        };
      }
      groups[dateKey].transactions.push(transaction);
      
      // Calcular totais (considerando receitas como positivo e despesas como negativo)
      const amount = parseFloat(transaction.amount);
      const signedAmount = transaction.type === 'income' ? amount : -amount;
      
      if (transaction.is_paid) {
        groups[dateKey].totalPaid += signedAmount;
      } else {
        groups[dateKey].totalPending += signedAmount;
      }
    });
    
    return groups;
  };

  const getCategory = (id) => {
    if (!id || !Array.isArray(categories) || categories.length === 0) return null;
    return categories.find((c) => c.id === id);
  };

  const getCategoryName = (id) => {
    const category = getCategory(id);
    return category ? category.name : '-';
  };

  const getAccount = (id) => {
    if (!id || !Array.isArray(accounts) || accounts.length === 0) return null;
    return accounts.find((a) => a.id === id);
  };

  const getAccountName = (id) => {
    const account = getAccount(id);
    return account ? account.name : '-';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Transações</h1>
        <div className="dashboard-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Visualização em lista"
            >
              <MdViewList size={20} />
              <span>Lista</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title="Visualização em calendário"
            >
              <MdCalendarViewMonth size={20} />
              <span>Calendário</span>
            </button>
          </div>
          <button 
            className={`btn-filter ${showOnlyPending ? 'active' : ''}`}
            onClick={() => setShowOnlyPending(!showOnlyPending)}
            title={showOnlyPending ? "Mostrar todas as transações" : "Mostrar apenas pendentes"}
          >
            {showOnlyPending ? (
              <>
                <MdFilterListOff size={18} />
                <span>Mostrar Todas</span>
              </>
            ) : (
              <>
                <MdFilterList size={18} />
                <span>Apenas Pendentes</span>
              </>
            )}
          </button>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Nova Transação
          </button>
        </div>
      </div>

      <div className="month-selector">
        <button onClick={previousMonth} className="month-btn">
          ◀
        </button>
        <h2>
          {format(currentDate, 'MMMM yyyy', { locale: ptBR }).replace(/^\w/, (c) =>
            c.toUpperCase()
          )}
        </h2>
        <button onClick={nextMonth} className="month-btn">
          ▶
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card income">
          <h3>Receitas</h3>
          <p className="amount">{formatCurrency(summary.income || 0)}</p>
        </div>
        <div className="summary-card expense">
          <h3>Despesas</h3>
          <p className="amount">{formatCurrency(summary.expense || 0)}</p>
        </div>
        <div className="summary-card balance">
          <h3>Saldo</h3>
          <p className="amount">{formatCurrency(summary.balance || 0)}</p>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingTransaction ? 'Editar' : 'Nova'} Transação</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingTransaction(null);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="transaction-form">
              <div className="transaction-form-body">
                <div className="form-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${formData.type === 'income' ? 'active income' : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, type: 'income', category_id: '' });
                    }}
                  >
                    <MdArrowUpward size={24} />
                    <span>Receita</span>
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.type === 'expense' ? 'active expense' : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, type: 'expense', category_id: '' });
                    }}
                  >
                    <MdArrowDownward size={24} />
                    <span>Despesa</span>
                  </button>
                </div>

                <div className="form-group with-icon">
                  <label>Descrição</label>
                  <div className="input-wrapper">
                    <MdDescription className="input-icon" size={20} />
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      required
                      placeholder="Ex: Compra no supermercado"
                      className={errors.description ? 'error' : ''}
                    />
                  </div>
                  {errors.description && (
                    <span className="error-message">{errors.description}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <CurrencyInput
                      value={formData.amount}
                      onChange={(amount) => setFormData({ ...formData, amount })}
                      label="Valor"
                      required
                      hasError={!!errors.amount}
                    />
                    {errors.amount && (
                      <span className="error-message">{errors.amount}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <DatePicker
                      value={formData.date}
                      onChange={(date) => setFormData({ ...formData, date })}
                      label="Data"
                      required
                      hasError={!!errors.date}
                    />
                    {errors.date && (
                      <span className="error-message">{errors.date}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <AccountSelect
                    accounts={accounts}
                    selectedId={formData.account_id}
                    onSelect={(accountId) => setFormData({ ...formData, account_id: accountId })}
                    label="Conta"
                    hasError={!!errors.account_id}
                  />
                  {errors.account_id && (
                    <span className="error-message">{errors.account_id}</span>
                  )}
                </div>

                <div className="form-group">
                  <CategorySelect
                    categories={categories}
                    selectedId={formData.category_id}
                    onSelect={(categoryId) => setFormData({ ...formData, category_id: categoryId })}
                    type={formData.type}
                    label="Categoria"
                    hasError={!!errors.category_id}
                  />
                  {errors.category_id && (
                    <span className="error-message">{errors.category_id}</span>
                  )}
                </div>

                <div className="form-group with-icon">
                  <label>Observações (opcional)</label>
                  <div className="input-wrapper">
                    <MdNotes className="input-icon textarea-icon" size={20} />
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleFormChange}
                      rows="3"
                      placeholder="Adicione observações sobre esta transação..."
                      className={errors.notes ? 'error' : ''}
                    />
                  </div>
                  {errors.notes && (
                    <span className="error-message">{errors.notes}</span>
                  )}
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="is_paid"
                    name="is_paid"
                    checked={formData.is_paid}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="is_paid">Pago</label>
                </div>

                {!editingTransaction && (
                  <>
                    <div className="form-group-checkbox">
                      <input
                        type="checkbox"
                        id="is_recurring"
                        name="is_recurring"
                        checked={formData.is_recurring}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="is_recurring">Transação Recorrente</label>
                    </div>

                    {formData.is_recurring && (
                      <div className="recurring-options">
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="frequency">Frequência</label>
                            <select
                              id="frequency"
                              name="frequency"
                              value={formData.frequency}
                              onChange={handleFormChange}
                              className={errors.frequency ? 'error' : ''}
                            >
                              <option value="daily">Diária</option>
                              <option value="weekly">Semanal</option>
                              <option value="biweekly">Quinzenal</option>
                              <option value="monthly">Mensal</option>
                              <option value="bimonthly">Bimestral</option>
                              <option value="quarterly">Trimestral</option>
                              <option value="semiannual">Semestral</option>
                              <option value="yearly">Anual</option>
                            </select>
                            {errors.frequency && (
                              <span className="error-message">{errors.frequency}</span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="occurrences">Quantidade</label>
                            <input
                              type="number"
                              id="occurrences"
                              name="occurrences"
                              value={formData.occurrences}
                              onChange={handleFormChange}
                              min="1"
                              max="120"
                              required={formData.is_recurring}
                              className={errors.occurrences ? 'error' : ''}
                            />
                            {errors.occurrences && (
                              <span className="error-message">{errors.occurrences}</span>
                            )}
                          </div>
                        </div>

                        <div className="recurring-info">
                          <p>
                            Serão geradas <strong>{formData.occurrences}</strong> transações{' '}
                            {formData.frequency === 'daily' && 'diárias'}
                            {formData.frequency === 'weekly' && 'semanais'}
                            {formData.frequency === 'biweekly' && 'quinzenais'}
                            {formData.frequency === 'monthly' && 'mensais'}
                            {formData.frequency === 'bimonthly' && 'bimensais'}
                            {formData.frequency === 'quarterly' && 'trimestrais'}
                            {formData.frequency === 'semiannual' && 'semestrais'}
                            {formData.frequency === 'yearly' && 'anuais'}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTransaction(null);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingTransaction ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="transactions-section">
        <div className="transactions-header">
          <h2>Transações do Mês</h2>
          {showOnlyPending && (
            <span className="filter-badge">
              <MdFilterList size={16} />
              Mostrando apenas pendentes
            </span>
          )}
        </div>
        {(showOnlyPending ? transactions.filter(t => !t.is_paid) : transactions).length === 0 ? (
          <div className="empty-state">
            {showOnlyPending ? (
              <>
                <MdCheckCircle size={48} style={{ color: 'var(--success-color)', marginBottom: '16px' }} />
                <p>Parabéns! Não há transações pendentes neste mês.</p>
              </>
            ) : (
              <p>Nenhuma transação encontrada para este mês.</p>
            )}
          </div>
        ) : (
          <>
            {viewMode === 'calendar' ? (
              <MonthlyTransactionCalendar
                transactions={showOnlyPending ? transactions.filter(t => !t.is_paid) : transactions}
                currentDate={currentDate}
                categories={categories}
                accounts={accounts}
                getCategory={getCategory}
                getAccount={getAccount}
                formatCurrency={formatCurrency}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePaid={togglePaid}
                onEffectuateVirtual={handleEffectuateVirtual}
                onInactivateVirtual={handleInactivateVirtual}
              />
            ) : (
              <div className="transactions-list">
            {Object.entries(groupTransactionsByDate(
              showOnlyPending 
                ? transactions.filter(t => !t.is_paid) 
                : transactions
            )).map(([date, dateGroup]) => (
              <div key={date} className="transaction-date-group">
                <div className="date-header">
                  <h3>{formatDateHeader(date)}</h3>
                  <div className="date-totals">
                    <span 
                      className={dateGroup.totalPaid >= 0 ? "total-paid" : "total-paid negative"}
                      title="Saldo dos valores efetivados (pagos)"
                    >
                      Saldo: {formatCurrency(dateGroup.totalPaid)}
                    </span>
                    {dateGroup.totalPending !== 0 && (
                      <span className="total-pending" title="Valores pendentes (não pagos)">
                        Pendente: {formatCurrency(Math.abs(dateGroup.totalPending))}
                      </span>
                    )}
                  </div>
                </div>
                
                {dateGroup.transactions.map((transaction) => {
                  const category = getCategory(transaction.category_id);
                  const account = getAccount(transaction.account_id);
                  const CategoryIcon = category ? getIconComponent(category.icon) : null;

                  return (
                    <div key={transaction.id} className={`transaction-item ${transaction.type}`}>
                      <div className="transaction-icon-wrapper">
                        <div
                          className="transaction-icon"
                          style={{ backgroundColor: category?.color || '#999' }}
                        >
                          {CategoryIcon && <CategoryIcon size={24} color="white" />}
                        </div>
                      </div>

                      <div className="transaction-content">
                        <div className="transaction-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h3 className="transaction-description">{transaction.description}</h3>
                            {transaction.is_virtual && (
                              <span 
                                style={{
                                  fontSize: '0.75rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: '#e3a864',
                                  color: '#fff',
                                  fontWeight: '600'
                                }}
                                title="Transação Virtual - Ainda não efetivada"
                              >
                                VIRTUAL
                              </span>
                            )}
                            {transaction.nature === 'recurring' && transaction.recurrence_id && (
                              <span 
                                style={{
                                  fontSize: '0.75rem',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: '#E3A864',
                                  color: '#fff',
                                  fontWeight: '600'
                                }}
                                title={`Transação Recorrente - ${transaction.current_occurrence || 1}/${transaction.occurrences || '?'}`}
                              >
                                🔄 {transaction.current_occurrence || 1}/{transaction.occurrences || '?'}
                              </span>
                            )}
                          </div>
                          <span className={`transaction-amount ${transaction.type}`}>
                            {transaction.type === 'income' ? '+' : '-'}
                            {formatCurrency(Math.abs(transaction.amount))}
                          </span>
                        </div>

                        <div className="transaction-meta">
                          <div className="transaction-category">
                            <span className="meta-label">Categoria:</span>
                            <span className="meta-value">{category?.name || '-'}</span>
                          </div>
                          <div className="transaction-account">
                            <span className="meta-label">Conta:</span>
                            <span className="meta-value">{account?.name || '-'}</span>
                          </div>
                          {transaction.nature === 'recurring' && transaction.frequency && (
                            <div className="transaction-frequency">
                              <span className="meta-label">Frequência:</span>
                              <span className="meta-value">
                                {transaction.frequency === 'daily' && 'Diária'}
                                {transaction.frequency === 'weekly' && 'Semanal'}
                                {transaction.frequency === 'biweekly' && 'Quinzenal'}
                                {transaction.frequency === 'monthly' && 'Mensal'}
                                {transaction.frequency === 'bimonthly' && 'Bimestral'}
                                {transaction.frequency === 'quarterly' && 'Trimestral'}
                                {transaction.frequency === 'semiannual' && 'Semestral'}
                                {transaction.frequency === 'yearly' && 'Anual'}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="transaction-footer">
                          <div className="transaction-status">
                            {transaction.is_paid ? (
                              <span className="status-badge paid">
                                <MdCheckCircle size={16} />
                                Pago
                              </span>
                            ) : (
                              <span className="status-badge pending">
                                <MdRadioButtonUnchecked size={16} />
                                Pendente
                              </span>
                            )}
                          </div>

                          <div className="transaction-actions">
                            {transaction.is_virtual ? (
                              // Ações para transações virtuais
                              <>
                                <button
                                  className="action-btn edit"
                                  onClick={() => handleEffectuateVirtual(transaction)}
                                  title="Efetivar Transação"
                                  style={{ backgroundColor: '#28a745', color: '#fff' }}
                                >
                                  <MdCheckCircle size={20} />
                                </button>
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleInactivateVirtual(transaction)}
                                  title="Inativar Transação Fixa"
                                  style={{ backgroundColor: '#ff9800', color: '#fff' }}
                                >
                                  <MdToggleOff size={20} />
                                </button>
                              </>
                            ) : (
                              // Ações para transações reais
                              <>
                                <button
                                  className="action-btn status-toggle"
                                  onClick={() => togglePaid(transaction)}
                                  title={transaction.is_paid ? 'Marcar como pendente' : 'Marcar como pago'}
                                >
                                  {transaction.is_paid ? (
                                    <MdRadioButtonUnchecked size={20} />
                                  ) : (
                                    <MdCheckCircle size={20} />
                                  )}
                                </button>
                                <button
                                  className="action-btn edit"
                                  onClick={() => handleEdit(transaction)}
                                  title="Editar"
                                >
                                  <MdEdit size={20} />
                                </button>
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleDelete(transaction.id, transaction.is_virtual)}
                                  title="Excluir"
                                >
                                  <MdDelete size={20} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`toast ${notification.type}`}>
            <div className="toast-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
              <h4 className="toast-title">{notification.title}</h4>
              <p className="toast-message">{notification.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeNotification(notification.id)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      {/* Effectuate Transaction Modal */}
      <EffectuateTransactionModal
        isOpen={effectuateModal.isOpen}
        onClose={() => setEffectuateModal({ isOpen: false, transaction: null })}
        onConfirm={handleEffectuateTransaction}
        transaction={effectuateModal.transaction}
        accounts={accounts}
      />
    </div>
  );
}

export default Transactions;
